"use client";
import { Box, Button, Grid, Typography } from "@mui/material";
import Image from "next/image";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import Slider from "react-slick";
import { ApiService } from "@/app/services/AppService";
import { getToken } from "@/app/services/HolderService";
import { ResponseObject, Subscription } from "@/app/classes/main";
import { getHubId } from "../genericComponents/helper-functions/currencyCode";
import { useCurrencyCode } from "../genericComponents/helper-functions/CurrencySymbol";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./index.css";
import Login from "../login/Login";
import Cookies from "js-cookie";
import ThankYouDialog from "./ThankYouDialog";
import { Loader } from "../genericComponents/MainLoader/Loader";

export const Plans = ({}) => {
  var settings = {
    infinite: true,
    speed: 1000,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: false,
    dots: true,
    className: "plansCarousel",
    autoplay: true,
    autoplaySpeed: 5000,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const [subscriptionData, setSubscriptionData] = useState<
    Subscription[] | undefined
  >(undefined);
  const [showLoginBox, setShowLoginBox] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const apiService = useMemo(() => new ApiService(), []);
  const token = getToken();
  const currencyCode = useCurrencyCode();
  const xHubId = getHubId();

  const getPlansData = useCallback(async () => {
    if (token) {
      try {
        const responseObject: ResponseObject | undefined =
          await apiService.get<ResponseObject>(
            apiService.subscriptionsWithLogin + "x-hubId=" + xHubId,
            token,
            xHubId
          );
        setSubscriptionData(responseObject?.object);
      } catch (error) {
        console.log("Error:", error);
      }
    } else {
      try {
        const responseObject: ResponseObject | undefined =
          await apiService.get<ResponseObject>(
            apiService.subscriptionsWithoutLogin,
            undefined,
            xHubId
          );
        setSubscriptionData(responseObject?.object);
      } catch (error) {
        console.log("Error:", error);
      }
    }
  }, [apiService, token, xHubId]);

  useEffect(() => {
    getPlansData();
  }, [getPlansData]);

  function handleUserLoggedIn() {
    localStorage.setItem("isAlreadyLogin", "true");
    Cookies.set("isAlreadyLogin", "true", {
      expires: 365,
      secure: true,
      sameSite: "Strict", // or 'Lax', depending on your use case
    });
    // setAlreadyLogin(true);
    window.location.reload();
  }

  const getSubscriptionApply = async (response: any, subscriptionId: any) => {
    try {
      const payload = {
        subscriptionId: subscriptionId,
        razorpayValidatePayment: {
          orderId: response.razorpay_order_id,
          paymentId: response.razorpay_payment_id,
          signature: response.razorpay_signature,
        },
        serviceIds: [],
        buildNumber: 100,
      };

      const validationResponse = await apiService.post<ResponseObject>(
        apiService.subscribedCustomer,
        payload,
        token!,
        xHubId
      );

      const result = await validationResponse;
      if (result?.status === "success") {
        setOpenDialog(true);
      } else {
        alert("Payment verification failed.");
      }
    } catch (error) {
      console.error("Error validating payment:", error);
      alert("Something went wrong while verifying the payment.");
    }
  };

  const validatePayment = async (
    response: any,
    amount: number,
    subscriptionId: any
  ) => {
    try {
      const payload = {
        orderId: response.razorpay_order_id,
        paymentId: response.razorpay_payment_id,
        razorpayOrder: {
          amount: amount,
          currency: "INR",
          id: response.razorpay_order_id,
          status: "captured",
        },
        signature: response.razorpay_signature,
      };

      const validationResponse = await apiService.post<ResponseObject>(
        apiService.razorpayValidation,
        payload,
        token!
      );

      const result = await validationResponse;
      if (result?.status === "success") {
        getSubscriptionApply(response, subscriptionId);
        setLoading(false);
      } else {
        alert("Payment verification failed.");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error validating payment:", error);
      alert("Something went wrong while verifying the payment.");
      setLoading(false);
    }
  };

  const handlePayment = (
    e: any,
    orderId: string,
    amount: number,
    subscriptionId: any
  ) => {
    setLoading(false);
    const url =
      "https://play-lh.googleusercontent.com/Xfvj857cLrYU8GpQczqQYldDIPXYk2ow-bCuFVWQ633NmcKKx0iZ_hC5EQM-VbXC-y97"; // Optional

    const options = {
      key: "rzp_live_OwSnlsNKdctwe8", // Replace with your Key ID
      amount: amount,
      currency: "INR",
      name: "Salon Tym",
      description: "Subscription Amount",
      image: url,
      order_id: orderId,
      handler: async (response: any) => {
        setLoading(true);
        await validatePayment(response, amount, subscriptionId);
      },
      // prefill: {
      //   name: "Pankaj Kumar",
      //   email: "pankajsing555@gmail.com",
      //   contact: "8384064378",
      // },
      theme: {
        color: "#15741a",
      },
    };
    const paymentObject = new (window as any).Razorpay(options);

    paymentObject.on("payment.failed", (response: any) => {
      alert(`Payment Failed: ${response.error.description}`);
    });

    paymentObject.open();
    e.preventDefault();
  };

  const loadRazorpaySDK = (
    orderId: string,
    amount: number,
    subscriptionId: any
  ) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = (e) => handlePayment(e, orderId, amount, subscriptionId);
    document.body.appendChild(script);
  };

  const handleCreateOrder = async (item: any) => {
    setLoading(true);
    try {
      const payload = {
        amount: item?.amountForCashback * 100,
        currency: "INR",
        receipt: "string",
      };
      const responseObject = await apiService.post<ResponseObject>(
        apiService.razorpayCreateOrder,
        payload,
        token!
      );
      if (responseObject?.status === "success") {
        const { id: orderId } = await responseObject?.object;
        loadRazorpaySDK(orderId, payload?.amount, item?.id);
      } else {
        setLoading(false);
        alert("Failed to create order. Please try again.");
      }
    } catch (error) {
      console.error("Error validating payment:", error);
      setLoading(false);
      alert("Something went wrong while verifying the payment.");
    }
  };

  return (
    <>
      {loading && <Loader />}
      {subscriptionData && subscriptionData?.length > 0 ? (
        <Box
          sx={{
            background:
              "linear-gradient(135deg, #102117 0%, #163726 38%, #5b2d1f 100%)",
            marginTop: 0,
            padding: { xs: "1.15rem 0 0.9rem", sm: "1.3rem 0 1rem" },
            borderRadius: { xs: "24px", sm: "30px" },
            width: "var(--salontym-content-width)",
            marginLeft: "auto",
            marginRight: "auto",
            marginBottom: "var(--salontym-section-gap)",
            boxShadow: "0 24px 56px rgba(11, 26, 19, 0.18)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              right: "-5rem",
              top: "-5rem",
              width: "16rem",
              height: "16rem",
              borderRadius: "999px",
              background:
                "radial-gradient(circle, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0) 72%)",
            }}
          />
          <Grid item container xs={12} sm={12} md={12} lg={12} margin="auto">
            <Box
              sx={{
                textAlign: "center",
                flexDirection: "column",
                alignItems: "center",
                width: "100% ",
              }}
            >
              <Box sx={{ textAlign: "center", color: "white", position: "relative", zIndex: 1 }}>
                <Box
                  className="section-eyebrow"
                  sx={{
                    margin: "0 auto 0.75rem",
                    background: "rgba(255,255,255,0.1)",
                    borderColor: "rgba(255,255,255,0.12)",
                    color: "#f7e7bf",
                  }}
                >
                  Memberships and savings
                </Box>
                <Typography
                  variant="body1"
                  className="choose-plan-title playball-regular"
                  sx={{
                    margin: "auto",
                    width: "fit-content",
                    fontWeight: "bold",
                    marginBottom: "5px",
                    color: "rgba(255,255,255,0.86)",
                  }}
                >
                  Choose Your Plan
                </Typography>
                <Typography
                  variant="body1"
                  className="lexend-heading"
                  sx={{
                    color: "white",
                    fontSize: { xs: "1.65rem", sm: "2.4rem" },
                    lineHeight: 1.05,
                    maxWidth: "20ch",
                    margin: "0 auto",
                    fontFamily: 'var(--salontym-font-display)',
                  }}
                >
                  Membership Benefits, Styled for Modern Self-Care
                </Typography>
                <Typography
                  sx={{
                    margin: "0.65rem auto 0",
                    maxWidth: "38rem",
                    color: "rgba(255,255,255,0.76)",
                    lineHeight: 1.68,
                    fontSize: "0.95rem",
                  }}
                >
                  Compare premium plans, unlock recurring value, and choose a
                  membership that keeps your beauty routine elevated.
                </Typography>
              </Box>
              <Box sx={{ margin: "1.5rem auto 1rem" }}>
                {subscriptionData && subscriptionData.length == 1 ? (
                  <>
                    {subscriptionData &&
                      subscriptionData.length > 0 &&
                      subscriptionData?.map((item, i) => (
                        <Box className="sliderContent" key={item?.id ?? i} sx={{ margin: "auto" }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "left",
                              alignItems: "center",
                            }}
                          >
                            <Image
                              src={item?.imageFile?.secureHttpPath}
                              alt="Logo"
                              style={{
                                width: "40px",
                                height: "auto",
                              }}
                              height={500}
                              width={500}
                            />
                            &nbsp; &nbsp;
                            <Typography
                              sx={{ fontWeight: "bold" }}
                              variant="h5"
                            >
                              {item?.heading}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              fontFamily: "Maestrale Pro",
                              fontSize: { xs: "14px", sm: "16px" },
                              margin: "8px 0px",
                              textAlign: "left",
                            }}
                            variant="body2"
                          >
                            {item?.subheading}
                          </Typography>
                          {item?.descriptions?.map(
                            (nesteditem: any, nestedIndex: any) => (
                              <Box
                                key={`${item?.id ?? "plan"}-feature-${nestedIndex}`}
                                sx={{
                                  display: "flex",
                                  justifyContent: "left",
                                  alignItems: "center",
                                }}
                              >
                                <CheckIcon
                                  fontSize="small"
                                  sx={{ color: "#15741a", paddingRight: "5px" }}
                                />
                                <Typography
                                  sx={{
                                    textAlign: "left",
                                    fontSize: { xs: "12px", sm: "13px" },
                                  }}
                                  variant="body2"
                                >
                                  {nesteditem}
                                </Typography>
                              </Box>
                            )
                          )}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              width: "100%",
                              margin: "16px 0px 8px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "left",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                }}
                                variant="body1"
                              >
                                {currencyCode}
                                {item?.amountForCashback}
                              </Typography>
                              &nbsp;&nbsp;
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  textDecoration: "line-through",
                                }}
                                variant="caption"
                              >
                                {currencyCode}
                                {item?.subscriptionMrp}
                              </Typography>
                              &nbsp;&nbsp;
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                }}
                                variant="body1"
                              >
                                <span
                                  style={{
                                    fontWeight: "normal",
                                    fontSize: "14px",
                                    marginRight: "5px",
                                  }}
                                >
                                  Save
                                </span>
                                {currencyCode}
                                {item?.subscriptionDiscount}
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              color="success"
                              sx={{
                                color: "white",
                                borderRadius: "999px",
                                textTransform: "none",
                                width: "auto",
                                boxShadow: "0 14px 26px rgba(21,116,26,0.22)",
                              }}
                              onClick={
                                token
                                  ? () =>
                                      handleCreateOrder(item?.amountForCashback)
                                  : () => setShowLoginBox(true)
                              }
                            >
                              Subscribe
                            </Button>
                          </Box>
                        </Box>
                      ))}
                  </>
                ) : (
                  <Slider {...settings}>
                    {subscriptionData &&
                      subscriptionData.length > 0 &&
                      subscriptionData?.map((item, i) => (
                        <Box className="sliderContent" key={item?.id ?? i}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "left",
                              alignItems: "center",
                            }}
                          >
                            <Image
                              src={item?.imageFile?.secureHttpPath}
                              alt="Logo"
                              style={{
                                width: "40px",
                                height: "auto",
                              }}
                              height={500}
                              width={500}
                            />
                            &nbsp; &nbsp;
                            <Typography
                              sx={{ fontWeight: "bold" }}
                              variant="h5"
                            >
                              {item?.heading}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              fontFamily: "Maestrale Pro",
                              fontSize: { xs: "14px", sm: "16px" },
                              margin: "8px 0px",
                              textAlign: "left",
                            }}
                            variant="body2"
                          >
                            {item?.subheading}
                          </Typography>
                          {item?.descriptions?.map(
                            (nesteditem: any, nestedIndex: any) => (
                              <Box
                                key={`${item?.id ?? "plan"}-feature-${nestedIndex}`}
                                sx={{
                                  display: "flex",
                                  justifyContent: "left",
                                  alignItems: "center",
                                }}
                              >
                                <CheckIcon
                                  fontSize="small"
                                  sx={{ color: "#15741a", paddingRight: "5px" }}
                                />
                                <Typography
                                  sx={{
                                    textAlign: "left",
                                    fontSize: { xs: "12px", sm: "13px" },
                                  }}
                                  variant="body2"
                                >
                                  {nesteditem}
                                </Typography>
                              </Box>
                            )
                          )}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              width: "100%",
                              margin: "16px 0px 8px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "left",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                }}
                                variant="body1"
                              >
                                {currencyCode}
                                {item?.amountForCashback}
                              </Typography>
                              &nbsp;&nbsp;
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  textDecoration: "line-through",
                                }}
                                variant="caption"
                              >
                                {currencyCode}
                                {item?.subscriptionMrp}
                              </Typography>
                              &nbsp;&nbsp;
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                }}
                                variant="body1"
                              >
                                <span
                                  style={{
                                    fontWeight: "normal",
                                    fontSize: "14px",
                                    marginRight: "5px",
                                  }}
                                >
                                  Save
                                </span>
                                {currencyCode}
                                {item?.subscriptionDiscount}
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              color="success"
                              sx={{
                                color: "white",
                                borderRadius: "999px",
                                textTransform: "none",
                                width: "auto",
                                boxShadow: "0 14px 26px rgba(21,116,26,0.22)",
                              }}
                              onClick={
                                token
                                  ? () => handleCreateOrder(item)
                                  : () => setShowLoginBox(true)
                              }
                            >
                              Subscribe
                            </Button>
                          </Box>
                        </Box>
                      ))}
                  </Slider>
                )}
              </Box>
            </Box>
          </Grid>
        </Box>
      ) : (
        ""
      )}
      <Login
        showLoginBox={showLoginBox}
        handleLoginClose={() => setShowLoginBox(false)}
        handleUserLoggedIn={handleUserLoggedIn}
      />
      <ThankYouDialog open={openDialog} onClose={() => setOpenDialog(false)} />
    </>
  );
};
